import { PretaxAccountV2Type, PretaxWalletStatus } from 'constants/accounts';
import moment from 'moment';
import dayjs from 'utils/date-helper';

export const getDefaultAccountName = (wallet_type_id: string) => {
  switch (wallet_type_id) {
    case PretaxAccountV2Type.DCFSA:
      return 'Dependent Care Flexible Spending Account (DCFSA)';
    case PretaxAccountV2Type.FSA:
      return 'Flexible Spending Account (FSA)';
    case PretaxAccountV2Type.HSA:
      return 'Health Savings Account (HSA)';
    case PretaxAccountV2Type.LPFSA:
      return 'Limited Purpose Flexible Spending Account (LPFSA)';
    case PretaxAccountV2Type.TRANSIT:
      return 'Transit';
    case PretaxAccountV2Type.PARKING:
      return 'Parking';
    default:
      return 'Unknown Pretax Product';
  }
};

export const getSupportLink = (wallet_type_id: PretaxAccountV2Type) => {
  switch (wallet_type_id) {
    case PretaxAccountV2Type.FSA:
      return 'https://support.joinforma.com/en/articles/4493355-how-does-a-health-care-fsa-fsa-work';
    case PretaxAccountV2Type.LPFSA:
      return 'https://support.joinforma.com/en/articles/4493415-how-does-a-limited-purpose-fsa-lpfsa-work';
    case PretaxAccountV2Type.DCFSA:
      return 'https://support.joinforma.com/en/articles/4481737-how-does-a-dependent-care-fsa-dcfsa-work';
    case PretaxAccountV2Type.PARKING:
    case PretaxAccountV2Type.TRANSIT:
      return 'https://support.joinforma.com/en/articles/4481775-how-does-a-commuter-benefits-accounts-work';
    case PretaxAccountV2Type.HSA:
      return 'https://support.joinforma.com/en/articles/4478835-health-care-fsa-hsa-or-hra-eligible-services';
    default:
      return 'https://support.joinforma.com/en/collections/2573463-pre-tax-accounts-faqs';
  }
};

export const getAccountTypeName = (wallet_type_id: PretaxAccountV2Type) => {
  switch (wallet_type_id) {
    case PretaxAccountV2Type.DCFSA:
      return 'DCFSA';
    case PretaxAccountV2Type.FSA:
      return 'FSA';
    case PretaxAccountV2Type.HSA:
      return 'HSA';
    case PretaxAccountV2Type.LPFSA:
      return 'LPFSA';
    case PretaxAccountV2Type.TRANSIT:
      return 'Transit';
    case PretaxAccountV2Type.PARKING:
      return 'Parking';
    default:
      return 'Unknown Pretax Product';
  }
};

export const getDefaultDescription = (
  wallet_type_id: string,
  company_id?: string
) => {
  switch (wallet_type_id) {
    case PretaxAccountV2Type.DCFSA:
      return 'Use pre-tax funds to pay for dependent care expenses.';
    case PretaxAccountV2Type.FSA:
      return 'Use pretax funds to pay for dental, vision, and medical expenses for you and your dependents.';
    case PretaxAccountV2Type.HSA:
      return 'Use pre-tax funds to pay for medical expenses for you and your dependents.';
    case PretaxAccountV2Type.LPFSA:
      return company_id === 'dmt' || company_id === 'shopify'
        ? 'Use pretax funds to pay for dental, vision and post-deductible medical expenses for you and your dependents.'
        : 'Use pretax funds to pay for dental and vision expenses for you and your dependents.';
    case PretaxAccountV2Type.TRANSIT:
      return 'Use pre-tax funds to pay for transit expenses while traveling to and from work.';
    case PretaxAccountV2Type.PARKING:
      return 'Use pre-tax funds to pay for parking expenses while traveling to and from work.';
    default:
      return 'Unknown Pretax Product';
  }
};

export const isActivePretaxWallet = (wallet: {
  status?: string;
  company_wallet_configuration: {
    active: boolean;
    start_date: string;
  };
}) =>
  wallet.company_wallet_configuration.active &&
  wallet.status === PretaxWalletStatus.ACTIVE;

export const isAccountStartDateSameAsEndDate = (wallet: {
  company_wallet_configuration: {
    start_date: string;
    end_date: string;
  };
}) =>
  wallet?.company_wallet_configuration?.start_date ===
  wallet?.company_wallet_configuration?.end_date;

export const getWalletDisplayName = ({
  walletName,
  walletTypeId,
  startDate,
  status,
  isActive,
}: {
  walletName: string;
  walletTypeId: string;
  startDate: string;
  isActive: boolean;
  status?: string;
}) => {
  const activeWalletDisplayName =
    walletName || getDefaultAccountName(walletTypeId);

  if (isActive) {
    return activeWalletDisplayName;
  }

  const inactiveInfo = moment(startDate).format('YYYY');

  return `(${inactiveInfo}) ${activeWalletDisplayName}`;
};

export const isActiveTempActivePretaxWallet = (wallet: {
  status?: string;
  company_wallet_configuration: {
    active?: boolean;
    start_date?: string;
  };
}) =>
  (wallet.status === PretaxWalletStatus.ACTIVE ||
    wallet.status === PretaxWalletStatus.TEMPORARILY_INACTIVE) &&
  wallet.company_wallet_configuration.active &&
  moment(wallet.company_wallet_configuration.start_date).isSameOrBefore(
    moment(),
    'day'
  );

export const isPretaxWallet = ({
  wallet_type_id,
}: {
  wallet_type_id: string;
}) => new Set<string>(Object.values(PretaxAccountV2Type)).has(wallet_type_id);

export const hasPretaxWallet = (
  wallets: Array<{
    wallet_type_id: string;
    visible_to_employee: boolean;
  }>,
  includeNotVisibleToEmployee = false
) =>
  !!wallets.find(({ wallet_type_id, visible_to_employee }) => {
    if (!visible_to_employee && !includeNotVisibleToEmployee) {
      return false;
    }

    return isPretaxWallet({ wallet_type_id });
  });

export const hasEmployerSponsoredWallet = (
  wallets: Array<{
    wallet_type_id: string;
    visible_to_employee: boolean;
  }>,
  includeNotVisibleToEmployee = false
) =>
  !!wallets.find(({ wallet_type_id, visible_to_employee }) => {
    if (!visible_to_employee && !includeNotVisibleToEmployee) {
      return false;
    }

    return !isPretaxWallet({ wallet_type_id });
  });

export const isDummyDate = (end_date: string | null) => {
  if (!end_date) {
    return false;
  }
  const end_date_year = dayjs(end_date).year();
  const current_year = dayjs().year();
  if (Math.abs(current_year - end_date_year) > 50) {
    return true;
  }
  return false;
};
